/**
 * SettingsModal — generieke modal-wrapper voor de Settings-dropdown.
 *
 * Vervangt op termijn de oude `/settings`-pagina door per-tab een eigen
 * popup-window. Houdt zelf één verantwoordelijkheid: backdrop + header +
 * close-knop + ESC-handling. Inhoud (Account, Quotas, etc.) wordt als
 * children doorgegeven — die componenten zijn al stand-alone uit
 * `apps/web/src/settings/`.
 *
 * ARIA-conventie: `role="dialog"` + `aria-modal="true"` + labelled-by
 * de header h2.
 *
 * ESC-handler werkt in capture-fase zodat nested confirm-dialogs (bv.
 * ConfirmDialog in WorkpackagesSettings) niet door ons "globale" ESC
 * dichtgeklapt worden vóór ze hun eigen handler hebben gehad. Wij
 * sluiten alleen wanneer ESC niet door iets in de modal-body wordt
 * opgepakt.
 */
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Tailwind max-width-class. Default `max-w-3xl`. Voor brede inhoud
   *  zoals Audit-log of Snapshots-lijst kan een grotere maat handig zijn. */
  maxWidth?: string;
  /** `ledger`: kader in de ledger-stijl dat de donkere modus van het OS volgt
   *  (Instellingen-schermen). */
  tone?: "ledger";
  /** Bij `tone="ledger"`: geen eigen marge rond de inhoud (die regelt dat zelf,
   *  zoals de zijnavigatie van Super admin). */
  flush?: boolean;
}

export function SettingsModal({
  title,
  onClose,
  children,
  maxWidth = "max-w-3xl",
  tone,
  flush,
}: Props) {
  const ledger = tone === "ledger";
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = `settings-modal-heading-${Math.random().toString(36).slice(2, 10)}`;

  // ESC-close. Skip wanneer focus in een editable veld zit — daar mag
  // ESC z'n eigen betekenis houden (bv. select-popover sluiten).
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isEditableTarget(e.target)) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Initial focus naar de modal-container zelf zodat keyboard-users
  // direct binnen de modal zitten. Geen volledige focus-trap (kost
  // veel code) — voor MVP is initial-focus + ESC-close voldoende.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-8"
      onMouseDown={(e) => {
        // Klik op backdrop sluit; klik in de dialog niet.
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={
          ledger
            ? `ledger w-full ${maxWidth} overflow-hidden rounded-xl border border-(--ld-control) bg-(--ld-surface) shadow-2xl outline-none`
            : `w-full ${maxWidth} rounded-lg border border-slate-300 bg-white shadow-2xl outline-none`
        }
      >
        <header
          className={
            ledger
              ? "flex items-center justify-between border-(--ld-border) border-b px-5 py-3"
              : "flex items-center justify-between border-slate-200 border-b px-5 py-3"
          }
        >
          <h2
            id={headingId}
            className={ledger ? "font-semibold text-base" : "font-semibold text-lg text-slate-900"}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={
              ledger
                ? "flex h-8 w-8 items-center justify-center rounded-md text-(--ld-muted) text-xl leading-none hover:bg-(--ld-raised) hover:text-(--ld-text)"
                : "rounded px-2 py-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }
            title={t("settings.common.close")}
            aria-label={t("settings.common.close")}
          >
            ×
          </button>
        </header>
        <div className={ledger ? (flush ? "" : "px-6 py-5") : "px-5 py-4"}>{children}</div>
      </div>
    </div>
  );
}
